import { serverMessageSchema, type ClientMessage, type ServerMessage } from '@voxa/shared';
import { RetryBudget } from './backoff.ts';

/**
 * The signalling socket. Reconnects within the 30-second budget of section 8
 * and then gives up rather than looping forever. Every inbound frame is parsed
 * with the shared schema before a listener sees it: the server is trusted, but
 * a proxy in the middle is not, and a malformed frame must not become state.
 */

export type SignalingStatus = 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface SignalingHandlers {
  onMessage(message: ServerMessage): void;
  onStatus(status: SignalingStatus): void;
  /** Fired once, when the retry budget is spent. */
  onGiveUp(): void;
}

const HEARTBEAT_MS = 10_000;

export class Signaling {
  readonly #url: string;
  readonly #handlers: SignalingHandlers;
  readonly #budget = new RetryBudget();
  #socket: WebSocket | null = null;
  #heartbeat: ReturnType<typeof setInterval> | null = null;
  #retryTimer: ReturnType<typeof setTimeout> | null = null;
  #stopped = false;

  constructor(roomId: string, handlers: SignalingHandlers) {
    const scheme = location.protocol === 'https:' ? 'wss:' : 'ws:';
    this.#url = `${scheme}//${location.host}/ws?room=${encodeURIComponent(roomId)}`;
    this.#handlers = handlers;
  }

  connect(): void {
    if (this.#stopped) return;
    this.#handlers.onStatus(this.#budget.attempt === 0 ? 'connecting' : 'reconnecting');

    const socket = new WebSocket(this.#url);
    this.#socket = socket;

    socket.addEventListener('open', () => {
      this.#budget.reset();
      this.#handlers.onStatus('open');
      this.send({ type: 'hello' });
      this.#startHeartbeat();
    });

    socket.addEventListener('message', (event) => {
      if (typeof event.data !== 'string') return;
      let json: unknown;
      try {
        json = JSON.parse(event.data);
      } catch {
        return;
      }
      const parsed = serverMessageSchema.safeParse(json);
      if (parsed.success) this.#handlers.onMessage(parsed.data);
    });

    socket.addEventListener('close', (event) => {
      this.#stopHeartbeat();
      if (this.#stopped) return;

      // 4001/4004 mean the room is gone or we were never in it. Retrying is
      // pointless and would just hammer a closed door.
      const permanent = event.code === 4001 || event.code === 4004 || event.code === 1000;
      if (permanent) {
        this.#handlers.onStatus('closed');
        this.#handlers.onGiveUp();
        return;
      }

      const delay = this.#budget.next();
      if (delay === null) {
        this.#handlers.onStatus('closed');
        this.#handlers.onGiveUp();
        return;
      }
      this.#handlers.onStatus('reconnecting');
      this.#retryTimer = setTimeout(() => this.connect(), delay);
    });

    socket.addEventListener('error', () => {
      // The close event always follows; handling it twice would double the
      // backoff for one failure.
    });
  }

  send(message: ClientMessage): void {
    if (this.#socket?.readyState === WebSocket.OPEN) {
      this.#socket.send(JSON.stringify(message));
    }
  }

  close(): void {
    this.#stopped = true;
    this.#stopHeartbeat();
    if (this.#retryTimer) clearTimeout(this.#retryTimer);
    this.#retryTimer = null;
    try {
      this.#socket?.close(1000, 'client closed');
    } catch {
      /* already gone */
    }
    this.#socket = null;
    this.#handlers.onStatus('closed');
  }

  #startHeartbeat(): void {
    this.#stopHeartbeat();
    this.#heartbeat = setInterval(() => this.send({ type: 'ping', at: Date.now() }), HEARTBEAT_MS);
  }

  #stopHeartbeat(): void {
    if (this.#heartbeat) clearInterval(this.#heartbeat);
    this.#heartbeat = null;
  }
}
